import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { DATA_SOURCE, IDataSource } from '@ep-om/project/data-sources/datasource.abstract';
import { QueryDataSource } from '@ep-om/project/data-sources/query.datasource';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

export const HIDEABLE_SWITCH_KEY_PREFIX = 'row_enabled_';

const specialFields = [
  'HIDEABLE_SWITCH'
] as const;

const commonFields = [
  'TEXT',
  'NUMBER',
  'DATE',
  'DATETIME',
  'TIME',
  'EMAIL',
  'SELECT',
  'CHECKBOX',
  'TEXTAREA',
  'CONFIGURABLE_ISSUE_FIELD',
  'IMAGE',
  'FILE'
] as const;

const fields = [
  ...specialFields,
  ...commonFields
] as const;

export type FieldsType = (typeof fields)[number];

const size = {
  XSMALL: 'fi-xsmall',
  SMALL: 'fi-small',
  MEDIUM: 'fi-normal',
  LARGE: 'fi-large',
  XLARGE: 'fi-xlarge',
  XXLARGE: 'fi-xxlarge'
} as const;

export type TFieldGroupExtended = FormlyFieldConfig & {
  omType: FieldsType;
  dataSource?: { name: string; filter: string; queryId?: string; optionValue?: string; optionLabel?: string };
  issueFieldTemplate?: string;
};
export interface IFormlyField extends FormlyFieldConfig {
  fieldGroup?: TFieldGroupExtended[];
}

export function fieldKeyValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const rowsFormArray = control.root as FormArray;
    const allKeys: string[] =
      rowsFormArray.controls?.flatMap((rowCtrl) => {
        const fieldFormArray = rowCtrl.get('formArray') as FormArray;
        return fieldFormArray.controls?.map((c) => c.get('key').value) || [];
      }) || [];
    const isNotValid = allKeys.filter((k) => k === control.value).length > 1;
    return isNotValid ? { invalidValue: true } : null;
  };
}

@UntilDestroy()
@Component({
  selector: 'formly-editor',
  templateUrl: './formly-editor.component.html',
  styleUrls: ['./formly-editor.component.scss']
})
export class FormlyEditorComponent implements OnInit, OnChanges {
  @Input() fields$: Observable<IFormlyField[]>;
  @Output() changeFields = new EventEmitter<{ formlyFields: IFormlyField[]; isValid: boolean }>();
  @Input() limitedEditing = false;

  typeFieldsOptions: { value: FieldsType; label: string }[] = commonFields    
    .map((f) => ({ value: f, label: f }))
    .sort((a, b) => a.label.localeCompare(b.label));

  typeSizeOptions: { value: string; label: string }[] = Object.entries(size).map(([type, className]) => ({ value: className, label: type }));

  querySelectOptions$: Observable<{ value: string; label: string }[]>;
  rows = this._fb.array([]);

  dataSources: { value: string; label: string }[];

  constructor(
    private _fb: FormBuilder,
    @Inject(DATA_SOURCE) private _dataSources: IDataSource[]
  ) {
    this.dataSources = _dataSources.map((ds) => ({ value: ds.name, label: ds.name }));
    this.querySelectOptions$ = (this._dataSources.find((ds) => ds.name === 'QUERY') as QueryDataSource).getOptionsForQuerySelect();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('limitedEditing' in changes) {
      this.rows.controls.forEach((rc) =>
        (rc.get('formArray') as FormArray).controls.forEach((fc) => {
          const keyCtrl = fc.get('key');
          if (this.limitedEditing) {
            keyCtrl.disable({ emitEvent: false });
          } else {
            keyCtrl.enable({ emitEvent: false });
          }
        })
      );
    }
  }

  ngOnInit(): void {
    if (this.fields$) {
      this.fields$
        .pipe(
          filter((fields) => !!fields),
          untilDestroyed(this)
        )
        .subscribe((fields) => {
          this.rows.clear();
          for (let i = 0; i < fields.length; i = i + 2) {
            const fieldTitle = fields[i];
            const fieldRow = fields[i + 1];
            this.rows.push(this._getRowFormGroup(fieldTitle, fieldRow));
          }
        });
    }
  }

  private _getTypeAndPropsByTypeSelected(typeSelected: FieldsType): {
    type: string;
    props?: any;
    className?: string;
    dataSource?: { name: string; filter: string };
    validators?: { validation: (ValidationErrors | string)[] };
  } {
    if (typeSelected === 'TEXT') {
      return { type: 'om-input', props: { type: 'text' } };
    }
    if (typeSelected === 'NUMBER') {
      return { type: 'om-input', className: 'fi-xsmall', props: { type: 'number' } };
    }
    if (typeSelected === 'TEXTAREA') {
      return { type: 'om-textarea' };
    }
    if (typeSelected === 'CHECKBOX') {
      return { type: 'checkbox-formly', className: 'fi-xsmall' };
    }
    if (typeSelected === 'SELECT') {
      return { type: 'om-select', props: { multiple: true, searchable: false }, dataSource: { name: null, filter: null } };
    }
    if (typeSelected === 'DATE') {
      return { type: 'date', props: { format: 'dd/MM/yyyy', showTime: false } };
    }
    if (typeSelected === 'TIME') {
      return { type: 'time', props: { format: 'HH:mm' } };
    }
    if (typeSelected === 'DATETIME') {
      return { type: 'date', props: { format: 'dd/MM/yyyy HH:mm', showTime: { nzFormat: 'HH:mm' } } };
    }
    if (typeSelected === 'EMAIL') {
      return { type: 'om-input', props: { type: 'email' }, validators: { validation: ['email-validation'] } };
    }
    if (typeSelected === 'CONFIGURABLE_ISSUE_FIELD') {
      return { type: 'om-input', props: { type: 'text', disabled: true } };
    }
    if (typeSelected === 'IMAGE') {
      return { type: 'foto-upload', className: 'fi-xlarge' };
    }
    if (typeSelected === 'FILE') {
      return { type: 'file-upload', className: 'fi-xlarge' };
    }
    return null;
  }

  private _getFieldFormGroup(fieldGroup: TFieldGroupExtended): FormGroup {
    const formGroup = this._fb.group({
      omType: this._fb.control(fieldGroup.omType),
      type: this._fb.control(fieldGroup.type),
      key: this._fb.control(fieldGroup.key, { validators: [Validators.required, fieldKeyValidator()] }),
      label: this._fb.control(fieldGroup.props.label, { validators: Validators.required }),
      placeholder: this._fb.control(fieldGroup.props.placeholder),
      size: this._fb.control(fieldGroup.className || size.SMALL),
      required: this._fb.control(fieldGroup.props.required),
      value: this._fb.control(fieldGroup)
    });
    if (fieldGroup.omType === 'CHECKBOX') {
      formGroup.removeControl('placeholder');
      formGroup.removeControl('required');
    }
    if (fieldGroup.omType === 'SELECT') {
      formGroup.addControl('options', this._fb.array(((fieldGroup.props.options || []) as any[]).map((opt) => this._getOptionFormGroup(opt) || [])));
      formGroup.addControl('multiple', this._fb.control(fieldGroup.props.multiple || false));
      formGroup.addControl('searchable', this._fb.control(fieldGroup.props.searchable || false));
      formGroup.addControl('dataSource', this._fb.control(fieldGroup.dataSource?.name || null));
      formGroup.addControl('filter', this._fb.control(fieldGroup.dataSource?.filter || null));
      formGroup.addControl('queryId', this._fb.control(fieldGroup.dataSource?.queryId || null, [Validators.required]));
      formGroup.addControl('optionValue', this._fb.control(fieldGroup.dataSource?.optionValue || null, [Validators.required]));
      formGroup.addControl('optionLabel', this._fb.control(fieldGroup.dataSource?.optionLabel || null, [Validators.required]));
    }
    formGroup.get('type').disable();
    if (this.limitedEditing) {
      formGroup.get('key').disable();
    }
    if (fieldGroup.omType === 'CONFIGURABLE_ISSUE_FIELD') {
      formGroup.removeControl('required');
      formGroup.addControl('issueField', this._fb.control(fieldGroup?.issueFieldTemplate));
      formGroup.get('issueField').setValidators([Validators.required]);
    }
    if (fieldGroup.omType === 'HIDEABLE_SWITCH') {
      formGroup.get('label').disable();
    }


    formGroup.valueChanges.subscribe((_value) => {
      const value = formGroup.getRawValue();
      const item: TFieldGroupExtended = value.value;
      item.key = value.key;
      item.props.label = value.label;
      item.props.placeholder = value.placeholder;
      item.className = value.size;
      item.props.required = value?.required || false;
      item.props.options = value.options;
      item.props.multiple = value.multiple;
      item.props.searchable = value.searchable;
      if (value?.issueField) {
        item.issueFieldTemplate = value.issueField.trim();
      }
      if (item.dataSource) {
        item.dataSource.name = value.dataSource;
        item.dataSource.filter = value.filter;
        item.dataSource.queryId = value.queryId;
        item.dataSource.optionValue = value.optionValue;
        item.dataSource.optionLabel = value.optionLabel;
      }
      formGroup.get('value').setValue(item, { emitEvent: false });
      this._nextChangeFields();
    });
    return formGroup;
  }

  private _getTitleControl(template: string): FormControl {
    const title = template ? template.replace("<div class='fi-title'>", '').replace('</div>', '') : null;
    const titleControl = this._fb.control(title);
    titleControl.valueChanges.subscribe((_) => {
      this._nextChangeFields();
    });
    return titleControl;
  }

  private _getRowFormGroup(fieldTitle?: IFormlyField, fieldRow?: IFormlyField): FormGroup {
    const formGroup = this._fb.group({
      titleCtrl: this._getTitleControl(fieldTitle?.template),
      typeFieldsCtrl: this._fb.control(this.typeFieldsOptions[0].value),
      formArray: this._fb.array(fieldRow?.fieldGroup.map((fg) => this._getFieldFormGroup(fg)) || [])
    });

    return formGroup;
  }

  private _getOptionFormGroup(option?: any): FormGroup {
    const formGroup = this._fb.group({
      label: this._fb.control(option?.label, Validators.required),
      value: this._fb.control(option?.value, Validators.required)
    });
    return formGroup;
  }

  public get fieldsObj(): IFormlyField[] {
    return this.rows.controls.flatMap<IFormlyField>((c) => {
      const title: string = c.get('titleCtrl').value;
      const fields: any[] = c.get('formArray').value;
      return [
        {
          template: title?.length > 0 ? `<div class='fi-title'>${title}</div>` : null
        },
        {
          props: {
            deactivable: {
              label: 'ToDo',
              siblingKeys: [],
            }
          },
          fieldGroup: fields.map((item) => item.value),
          fieldGroupClassName: 'fg',
          hidden: c.get('hiddenCtrl')?.value || false
        }
      ];
    });
  }

  private _nextChangeFields() {
    this.changeFields.next({ formlyFields: this.fieldsObj, isValid: this.rows.valid });
  }

  private getFormArrayByRow(iRow: number): FormArray {
    return this.rows.at(iRow).get('formArray') as FormArray;
  }

  addField(iRow: number) {
    const typeSelected = this.rows.at(iRow).get('typeFieldsCtrl').value as FieldsType;
    const { type, props, className, dataSource, validators } = this._getTypeAndPropsByTypeSelected(typeSelected);
    const iField = this.getFormArrayByRow(iRow).length;
    const field: TFieldGroupExtended = {
      type,
      omType: typeSelected,
      key: `key_${iRow + 1}_${iField + 1}`,
      className: className || 'fi-small',
      props: {
        ...(props || {}),
        label: 'label',
        placeholder: null,
        required: true,
        options: []
      },
      dataSource,
      validators
    };
    this.getFormArrayByRow(iRow).push(this._getFieldFormGroup(field));
    this._nextChangeFields();
  }

  removeField(iRow: number, iField: number) {
    this.getFormArrayByRow(iRow).removeAt(iField);
    this._nextChangeFields();
  }

  /**
   * move the field in the form
   * @param iRow index row
   * @param iField index filed
   * @param direction up or down move field between row, left or right move field within row
   */
  moveField(iRow: number, iField: number, direction: 'up' | 'down' | 'left' | 'right') {
    let fromIndex: number;
    let toIndex: number;
    if (direction === 'up' || direction === 'down') {
      fromIndex = iRow;
      toIndex = direction === 'up' ? fromIndex - 1 : fromIndex + 1;
      const sourceFormArray = this.getFormArrayByRow(fromIndex);
      const targetFormArray = this.getFormArrayByRow(toIndex);
      const itemToMove = sourceFormArray.at(iField);
      sourceFormArray.removeAt(iField);
      targetFormArray.push(itemToMove);
    }
    if (direction === 'left' || direction === 'right') {
      fromIndex = iField;
      toIndex = direction === 'left' ? fromIndex - 1 : fromIndex + 1;
      const fieldsFormArray = this.getFormArrayByRow(iRow);
      const itemToMove = fieldsFormArray.at(fromIndex);
      fieldsFormArray.removeAt(fromIndex);
      fieldsFormArray.insert(toIndex, itemToMove);
    }
    this._nextChangeFields();
  }

  addRow(isHideable?: boolean) {
    const rowFormGroup: FormGroup = this._getRowFormGroup();
    if (isHideable) {
      (rowFormGroup.get('formArray') as FormArray).push(this._deactivableFieldGroup);
    }   
    this.rows.push(rowFormGroup);
    this._nextChangeFields();
  }

  private get _deactivableFieldGroup(): FormGroup {
    const deactivableField: TFieldGroupExtended = 
      {
        type: 'checkbox-formly',
        omType: 'HIDEABLE_SWITCH',
        key: `${HIDEABLE_SWITCH_KEY_PREFIX}${this.rows.length + 1}`,
        className: 'fi-xxlarge',
        props: {
          hidden: true
        },
        defaultValue: true,
      };

    return this._getFieldFormGroup(deactivableField);
  }

  removeRow(iRow: number) {
    this.rows.removeAt(iRow);
    this._nextChangeFields();
  }

  getCollapseTitle(iRow: number): string {
    return `ROW ${iRow + 1} [${(this.getFormArrayByRow(iRow).getRawValue() as any[]).map((field) => field.omType)}]`;
  }

  addOption(iRow: number, iField: number) {
    const optionsFormArray = this.getFormArrayByRow(iRow).at(iField).get('options') as FormArray;
    optionsFormArray.push(this._getOptionFormGroup());
    this._nextChangeFields();
  }

  removeOption(iRow: number, iField: number, iOption: number) {
    const optionsFormArray = this.getFormArrayByRow(iRow).at(iField).get('options') as FormArray;
    optionsFormArray.removeAt(iOption);
    this._nextChangeFields();
  }
}
